import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, Copy, Loader2, Mic, MicOff, Pause, Play, RotateCw, Square, X } from "lucide-react";
import { toast } from "sonner";

import { listSuggestions, type InRoom, type Meeting } from "@/api/meetings";
import { ElapsedTimer } from "@/components/ring-central/ongoing-call-indicator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";

import type { LiveLine } from "./capture";
import { RecordingDot } from "./meeting-controls";
import { useMeetingMode } from "./meeting-mode";
import { processingLabel } from "./processing-label";

/** The line to read aloud when recording starts. */
export const CONSENT_LINE = "Kory is taking notes for this meeting.";

/** The notice to paste into Meet chat (decision 13: internal meetings only in v1). */
export function meetChatNotice(name: string | null | undefined): string {
  const who = name?.trim().split(/\s+/)[0] || "I";
  const whose = who === "I" ? "me" : who;
  return `${who} ${who === "I" ? "am" : "is"} taking notes with Kory (HickoryOS) for this meeting. The transcript and audio stay private to ${whose}.`;
}

const IN_ROOM_HINT: Record<InRoom, string> = {
  alone: "The call is live text; your own words are transcribed after the meeting.",
  others: "Everyone near your mic is live text too.",
};

/**
 * The talk unit in Meeting mode: the one voice button, Listen, which becomes the recording control
 * (red edge, the timer, Pause and Stop). The gear menu's Meeting mode item switches back to
 * Personal. Kory is silent; the call streams as live text, notes go into the meeting, and a held Tell Kory is the one spoken command. Interrupted (capture
 * stopped, the meeting didn't) gets an amber edge and Resume Recording instead. Following (another tab
 * records) keeps the timer, the notes and Stop; the rest of the controls live in the recording tab.
 */
export function MeetingUnit() {
  const m = useMeetingMode();
  // While starting, the start sheet shows; the recording control takes over once capture runs.
  const live = m.live && m.phase !== "starting";
  const interrupted = m.phase === "interrupted";
  return (
    <div
      data-testid="meeting-unit"
      data-phase={m.phase}
      className={cn(
        "rounded-2xl border bg-background/85 p-2 shadow-sm",
        interrupted
          ? "border-warning shadow-[0_0_0_3px_color-mix(in_oklab,var(--warning)_22%,transparent)]"
          : live
            ? "border-destructive/70 shadow-[0_0_0_3px_color-mix(in_oklab,var(--destructive)_14%,transparent)]"
            : "border-destructive/30",
      )}
    >
      {m.phase === "rest" ? <AtRest /> : null}
      {m.phase === "sheet" || m.phase === "starting" ? <StartSheet /> : null}
      {live && m.meeting ? <Recording meeting={m.meeting} /> : null}
      {m.phase === "after" && m.meeting ? <After meeting={m.meeting} /> : null}
      {m.error ? (
        <p role="alert" className="mt-2 px-1 text-sm text-destructive">
          {m.error}
        </p>
      ) : null}
    </div>
  );
}

function AtRest() {
  const { openSheet } = useMeetingMode();
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        data-testid="meeting-listen"
        className="h-11 gap-2 rounded-full border-destructive/50 px-4 font-semibold text-destructive hover:bg-destructive/10 hover:text-destructive"
        onClick={openSheet}
      >
        <RecordingDot paused />
        Listen
      </Button>
      <p className="min-w-0 flex-1 text-sm leading-snug text-muted-foreground">Kory records this meeting and stays silent.</p>
    </div>
  );
}

function StartSheet() {
  const { start, closeSheet, phase } = useMeetingMode();
  const [title, setTitle] = useState("");
  const [inRoom, setInRoom] = useState<InRoom>("alone");
  const starting = phase === "starting";
  return (
    <form
      data-testid="meeting-start-sheet"
      className="space-y-3 p-1"
      onSubmit={(event) => {
        event.preventDefault();
        void start({ title, inRoom });
      }}
    >
      <p className="text-sm font-semibold">Listen to this meeting</p>
      <Input
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder="Title (optional)"
        aria-label="Meeting title"
        maxLength={500}
        className="h-11"
      />
      <fieldset>
        <legend className="mb-1.5 text-xs font-semibold text-muted-foreground">In the room with you</legend>
        <InRoomChoice value={inRoom} onChange={setInRoom} />
        <p className="mt-1.5 text-xs text-muted-foreground">{IN_ROOM_HINT[inRoom]}</p>
      </fieldset>
      <p className="rounded-lg bg-muted/60 px-3 py-2 text-sm leading-snug">
        Next, Chrome asks what to share. Pick the <span className="font-semibold">Google Meet tab</span> and turn on{" "}
        <span className="font-semibold">Also share tab audio</span>. Headphones keep the call from being heard twice.
      </p>
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" className="h-11" onClick={closeSheet} disabled={starting}>
          Cancel
        </Button>
        <Button
          type="submit"
          data-testid="meeting-start"
          className="h-11 gap-2 bg-destructive text-white hover:bg-destructive/90"
          disabled={starting}
          aria-busy={starting}
        >
          {starting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <RecordingDot paused className="bg-white" />}
          {starting ? "Starting…" : "Start Listening"}
        </Button>
      </div>
    </form>
  );
}

function InRoomChoice({ value, onChange, small = false }: { value: InRoom; onChange: (v: InRoom) => void; small?: boolean }) {
  const options: [InRoom, string][] = [
    ["alone", "Just me"],
    ["others", "Others too"],
  ];
  return (
    <div role="radiogroup" aria-label="In the room with you" className="inline-flex rounded-lg border bg-muted/40 p-0.5">
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={value === v}
          onClick={() => onChange(v)}
          className={cn(
            "rounded-md px-3 font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            small ? "h-8 text-xs" : "h-10 text-sm",
            value === v ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Recording({ meeting }: { meeting: Meeting }) {
  const m = useMeetingMode();
  const { capture } = m;
  const paused = m.paused;
  const interrupted = m.phase === "interrupted";
  const follower = m.role === "follower";
  const stopping = m.phase === "stopping";
  // Audio that failed to save stays flagged for the rest of the meeting: a Resume starts a fresh
  // recorder, which would otherwise clear the channel's error.
  const [lostAudio, setLostAudio] = useState<{ id: string; channel: "mic" | "tab" } | null>(null);
  const failedChannel = capture.channels.tab.audio === "error" ? "tab" : capture.channels.mic.audio === "error" ? "mic" : null;
  useEffect(() => {
    if (failedChannel) setLostAudio((lost) => (lost?.id === meeting.id ? lost : { id: meeting.id, channel: failedChannel }));
  }, [failedChannel, meeting.id]);
  const lost = lostAudio?.id === meeting.id ? lostAudio.channel : null;
  // Live text that can't run or can't be kept says so, rather than leaving the tray looking live.
  const liveOff =
    capture.status !== "recording"
      ? null
      : capture.channels.tab.live === "error" || capture.channels.mic.live === "error"
        ? "Live text is off: Kory can't reach the transcriber and keeps trying."
        : !capture.liveSaving
          ? "Live text isn't being saved right now."
          : null;
  // The transcript after the meeting comes from the audio, so only promise it while the audio is saving.
  const audioSaving =
    !lost &&
    capture.channels.mic.audio !== "error" &&
    capture.channels.tab.audio !== "error" &&
    (capture.channels.mic.audio === "recording" || capture.channels.tab.audio === "recording");
  return (
    <div className="@container space-y-2">
      {/* Fits the tray at any timer length: the status word goes first, and only a very narrow card wraps Pause and Stop. */}
      <div className="flex flex-wrap items-center gap-2">
        {/* The one voice button, now the recording control. */}
        <div
          data-testid="meeting-listen"
          role="status"
          aria-label={
            interrupted
              ? "Not listening. Capture stopped"
              : follower
                ? `${paused ? "Recording paused" : "Listening"} in another tab`
                : paused
                  ? "Recording paused"
                  : "Listening"
          }
          className={cn(
            "flex h-11 min-w-0 items-center gap-2 rounded-full border px-3 text-sm font-semibold whitespace-nowrap",
            interrupted ? "border-warning bg-warning/15 text-foreground" : "border-destructive/50 bg-destructive/10 text-destructive",
          )}
        >
          {interrupted ? (
            <>
              <MicOff className="size-4" aria-hidden />
              <span>Not Listening</span>
            </>
          ) : (
            <>
              <RecordingDot paused={paused} />
              <ElapsedTimer since={meeting.started_at} />
              <span className="hidden @[26rem]:inline">{paused ? "Paused" : "Listening"}</span>
            </>
          )}
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {interrupted || follower ? null : (
            <Button
              type="button"
              variant="ghost"
              className="size-11 shrink-0 p-0"
              aria-label={paused ? "Resume" : "Pause"}
              title={paused ? "Resume" : "Pause"}
              disabled={stopping}
              onClick={paused ? capture.resume : capture.pause}
            >
              {paused ? <Play className="size-4" aria-hidden /> : <Pause className="size-4" aria-hidden />}
            </Button>
          )}
          <StopButton />
        </div>
      </div>
      {/* The row is the controls; at tray width the title needs its own line. */}
      <p className="truncate px-1 text-sm font-semibold" title={meeting.title}>
        {meeting.title}
      </p>
      {interrupted ? (
        <div data-testid="meeting-interrupted" className="flex flex-wrap items-center gap-2 rounded-lg border border-warning/60 bg-warning/10 px-3 py-2">
          <p className="min-w-0 flex-1 basis-48 text-sm">
            Kory stopped listening when its tab reloaded or closed. What was heard is saved.
          </p>
          <Button type="button" className="h-11 gap-1.5" onClick={() => void m.resumeRecording()}>
            <RotateCw className="size-4" aria-hidden />
            Resume Recording
          </Button>
        </div>
      ) : follower ? (
        <p data-testid="meeting-follower" className="rounded-lg bg-muted/60 px-3 py-2 text-sm">
          Recording in another tab. Live text and Tell Kory are there.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">Kory is silent</span>
            <InRoomChoice value={meeting.in_room} onChange={m.setInRoom} small />
          </div>
          {lost ? (
            <p data-testid="meeting-audio-failed" role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
              Some of the {lost === "tab" ? "call's" : "microphone's"} audio wasn&apos;t saved. Live text continues; if it
              keeps happening, Stop and start again.
            </p>
          ) : null}
          {liveOff ? (
            <p data-testid="meeting-live-off" role="alert" className="rounded-lg bg-warning/15 px-3 py-2 text-sm">
              {liveOff}
              {audioSaving ? " The audio is still recording, so the full transcript comes after the meeting." : null}
            </p>
          ) : null}
          {capture.status === "recording" && !capture.tabShared && canShareTab() ? (
            <p data-testid="meeting-no-tab" role="status" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
              Only your microphone is recording, so the call isn&apos;t heard. To record it, Stop and start again, then
              pick the Meet tab with <span className="font-semibold">Also share tab audio</span> on.
            </p>
          ) : null}
          <ConsentLine />
          <LiveTranscript lines={capture.lines} meeting={meeting} />
        </>
      )}
      <NoteBox meeting={meeting} readOnly={follower} />
      {interrupted || follower ? null : <TellKory disabled={paused || stopping} />}
    </div>
  );
}

function StopButton() {
  const { stopAsked, askStop, stop, phase } = useMeetingMode();
  const stopping = phase === "stopping";
  return (
    <Button
      type="button"
      variant="outline"
      data-testid="meeting-stop"
      aria-busy={stopping}
      disabled={stopping}
      className={cn(
        "h-11 shrink-0 gap-1.5 border-destructive/40 px-2.5 text-destructive hover:bg-destructive/10 hover:text-destructive",
        stopAsked && "bg-destructive text-white hover:bg-destructive/90 hover:text-white",
      )}
      onClick={() => (stopAsked ? void stop() : askStop())}
    >
      {stopping ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : <Square className="size-3 fill-current" aria-hidden />}
      {stopping ? "Stopping…" : stopAsked ? "Tap to Stop" : "Stop"}
    </Button>
  );
}

function ConsentLine() {
  const { meeting } = useMeetingMode();
  const { user } = useAuth();
  const [hidden, setHidden] = useState<string | null>(null);
  if (!meeting || hidden === meeting.id) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(meetChatNotice(user?.display_name));
      toast.success("Copied. Paste it into Meet chat.");
    } catch {
      toast.error("Couldn't copy. Select the line and copy it yourself.");
    }
  };
  return (
    <div data-testid="meeting-consent" className="flex items-start gap-2 rounded-lg bg-muted/60 py-1.5 pr-1 pl-3">
      <p className="min-w-0 flex-1 py-1 text-sm leading-snug">
        Say: <span className="font-semibold">“{CONSENT_LINE}”</span>
      </p>
      <Button type="button" variant="outline" className="h-9 shrink-0 gap-1.5 px-2.5 text-xs" onClick={() => void copy()}>
        <Copy className="size-3.5" aria-hidden />
        Copy for Meet Chat
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="size-9 shrink-0 p-0 text-muted-foreground"
        aria-label="Hide the consent line"
        onClick={() => setHidden(meeting.id)}
      >
        <X className="size-4" aria-hidden />
      </Button>
    </div>
  );
}

/** The tray shows the tail of the meeting; the whole transcript is on the notes page. */
const KEEP = 50;

/** Who said a live line: the call, or you (with others in the room, the room). */
function speaker(line: LiveLine, inRoom: InRoom): string {
  if (line.channel === "tab") return "Call";
  return inRoom === "alone" ? "You" : "Room";
}

export function LiveTranscript({ lines, meeting }: { lines: LiveLine[]; meeting: Meeting }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const alone = meeting.in_room === "alone";
  // A held Tell Kory is a command to Kory, not part of the meeting.
  const shown = lines.filter((l) => !l.tellKory).slice(-KEEP);
  // Live text follows the newest line, unless you scrolled up to read.
  const [following, setFollowing] = useState(true);
  const followingRef = useRef(true);
  const lastTop = useRef(0);
  const touchY = useRef<number | null>(null);
  const follow = (on: boolean) => {
    followingRef.current = on;
    setFollowing(on);
  };
  const toLive = useCallback(() => {
    const box = boxRef.current;
    if (!box || !followingRef.current) return;
    box.scrollTop = box.scrollHeight;
    lastTop.current = box.scrollTop;
  }, []);
  // Any line can change (an interim line under the newest, the window dropping old ones), so catch up after every render.
  useLayoutEffect(toLive);
  // Layout alone (the tray resizing, lines rewrapping) changes the height too.
  useEffect(() => {
    const text = textRef.current;
    if (!text || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => toLive());
    observer.observe(text);
    return () => observer.disconnect();
  }, [toLive]);
  return (
    <div>
      <div className="relative">
        <div
          ref={boxRef}
          data-testid="meeting-transcript"
          aria-live="polite"
          // A gesture up stops following at once, before a render could pull you back to the newest line.
          onWheel={(event) => {
            const box = event.currentTarget;
            if (event.deltaY < 0 && box.scrollHeight > box.clientHeight) follow(false);
          }}
          onTouchStart={(event) => {
            touchY.current = event.touches[0]?.clientY ?? null;
          }}
          onTouchMove={(event) => {
            const box = event.currentTarget;
            const y = event.touches[0]?.clientY ?? null;
            if (touchY.current !== null && y !== null && y > touchY.current + 2 && box.scrollHeight > box.clientHeight) follow(false);
            touchY.current = y;
          }}
          onScroll={(event) => {
            // Following's own scrolls and growing text only ever move down, so only a scroll up stops following;
            // the browser's own shifts (dropping the oldest line) are put back at the bottom before they arrive.
            // Scrolling back down follows again near the bottom, since lines keep landing while you get there.
            const box = event.currentTarget;
            const fromBottom = box.scrollHeight - box.scrollTop - box.clientHeight;
            const up = box.scrollTop < lastTop.current;
            lastTop.current = box.scrollTop;
            if (fromBottom <= 1 || (!up && fromBottom < box.clientHeight / 2)) follow(true);
            else if (up) follow(false);
          }}
          className="max-h-40 overflow-y-auto overscroll-contain px-1"
        >
          <div ref={textRef} className="space-y-1">
            {shown.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {alone ? "The call's words show here as people speak." : "Live text shows here as people speak."}
              </p>
            ) : (
              shown.map((l) => (
                <p key={l.id} className={cn("text-sm leading-snug", !l.final && "text-muted-foreground")}>
                  <span className={cn("mr-1.5 text-xs font-semibold uppercase tracking-wide", l.channel === "tab" ? "text-primary" : "text-foreground")}>
                    {speaker(l, meeting.in_room)}
                  </span>
                  {l.text}
                </p>
              ))
            )}
          </div>
        </div>
        {following ? null : (
          <Button
            type="button"
            variant="secondary"
            data-testid="meeting-jump-live"
            className="absolute bottom-1 left-1/2 h-8 -translate-x-1/2 gap-1 rounded-full px-3 text-xs shadow-md"
            onClick={() => {
              follow(true);
              toLive();
            }}
          >
            <ArrowDown className="size-3.5" aria-hidden />
            Jump to Live
          </Button>
        )}
      </div>
      {alone ? (
        <p data-testid="meeting-alone-line" className="mt-1 flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
          <Mic className="size-3.5" aria-hidden />
          Your words are transcribed after the meeting.
        </p>
      ) : null}
    </div>
  );
}

/** Your own notes in this meeting, newest last: the lines the note box added. */
function noteLines(notes: string | null): string[] {
  return (notes ?? "").split("\n").filter((l) => l.startsWith("- [")).map((l) => l.slice(2));
}

/** `readOnly`: a following tab shows the recent notes; the recording tab writes them. */
function NoteBox({ meeting, readOnly = false }: { meeting: Meeting; readOnly?: boolean }) {
  const { addNote, phase } = useMeetingMode();
  // Stopping: Kory composes the notes from what has landed, so the box closes.
  const closed = phase === "stopping";
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const recent = noteLines(meeting.notes_md).slice(-3);
  const submit = async () => {
    const text = draft.trim();
    if (!text || saving || closed) return;
    setSaving(true);
    setFailed(false);
    const saved = await addNote(text);
    setSaving(false);
    if (saved) setDraft("");
    else setFailed(true);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };
  return (
    <div className="space-y-1">
      {recent.length ? (
        <ul data-testid="meeting-notes" className="space-y-0.5 px-1">
          {recent.map((line, i) => (
            <li key={`${i}-${line}`} className="truncate text-xs text-muted-foreground">
              {line}
            </li>
          ))}
        </ul>
      ) : null}
      {readOnly ? (
        <p data-testid="meeting-notes-elsewhere" className="px-1 text-xs text-muted-foreground">
          Add notes in the recording tab.
        </p>
      ) : (
        <div className="flex items-center gap-1.5">
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Add a note, to-do: or idea:"
            aria-label="Add a note"
            disabled={closed}
            className="h-11 min-w-0 flex-1 text-base md:text-sm"
          />
          <Button type="button" className="h-11 shrink-0" disabled={!draft.trim() || saving || closed} aria-busy={saving} onClick={() => void submit()}>
            Add
          </Button>
        </div>
      )}
      {failed ? (
        <p role="alert" className="px-1 text-xs text-destructive">
          That note didn&apos;t save. It&apos;s still in the box.
        </p>
      ) : null}
    </div>
  );
}

/** A browser that can share a tab's audio (desktop Chrome); a phone records its mic only. */
function canShareTab(): boolean {
  return typeof navigator.mediaDevices?.getDisplayMedia === "function";
}

/** Hold to Tell Kory: only the held window of your mic is heard, and the words land in the meeting's notes. */
function TellKory({ disabled }: { disabled: boolean }) {
  const { capture, addNote } = useMeetingMode();
  const tell = capture.tellKory;
  const down = () => {
    if (!disabled && !tell.holding) tell.press();
  };
  const up = async () => {
    if (!tell.holding) return;
    const words = (await tell.release())?.trim();
    if (!words) {
      toast.message("Kory didn't hear anything. Hold and speak, then let go.");
      return;
    }
    if (await addNote(`Kory: ${words}`, { source: "tell_kory", text: words })) toast.success("Added to the meeting's notes.");
    else toast.error(`Couldn't save “${words}”. Type it in the note box.`);
  };
  return (
    <Button
      type="button"
      variant={tell.holding ? "default" : "outline"}
      data-testid="meeting-tell-kory"
      aria-pressed={tell.holding}
      disabled={disabled}
      className="h-11 w-full touch-none gap-2 font-semibold select-none"
      onPointerDown={down}
      onPointerUp={() => void up()}
      onPointerLeave={() => void up()}
      onPointerCancel={() => void up()}
      onKeyDown={(event) => {
        if ((event.key === " " || event.key === "Enter") && !event.repeat) {
          event.preventDefault();
          down();
        }
      }}
      onKeyUp={(event) => {
        if (event.key === " " || event.key === "Enter") void up();
      }}
    >
      <Mic className="size-4" aria-hidden />
      {tell.holding ? "Telling Kory… release to send" : "Hold to Tell Kory"}
    </Button>
  );
}

function After({ meeting }: { meeting: Meeting }) {
  const { done, stoppedBy } = useMeetingMode();
  const ready = meeting.status === "ready";
  const failed = meeting.status === "failed";
  const suggestions = useQuery({
    queryKey: ["meetings", meeting.id, "suggestions", "notes-ready-counts"],
    queryFn: async () => (await listSuggestions(meeting.id, { limit: 500 })).suggestions,
    enabled: ready,
  });
  const added = suggestions.data?.filter((row) => row.state === "added").length ?? 0;
  const waiting = suggestions.data?.filter((row) => row.state === "suggested").length ?? 0;
  const counts = added || waiting ? ` ${added} added · ${waiting} waiting.` : "";
  return (
    <div data-testid="meeting-after" className="flex flex-wrap items-center gap-2 p-1">
      <div className="min-w-0 flex-1 basis-48">
        <p className="truncate text-sm font-semibold">{meeting.title}</p>
        {stoppedBy ? (
          <p data-testid="meeting-stopped-by" className="text-xs text-muted-foreground">
            Stopped from another {stoppedBy === "another-tab" ? "tab" : "device"}.
          </p>
        ) : null}
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="meeting-after-status">
          {ready || failed ? null : <Loader2 className="size-3.5 animate-spin" aria-hidden />}
          {ready ? `Notes ready.${counts}` : failed ? "Kory couldn't write the notes. Your typed notes are saved." : processingLabel(meeting)}
        </p>
        {ready || failed ? null : (
          <p className="text-xs text-muted-foreground">Kory keeps working. Notes Ready lands here.</p>
        )}
      </div>
      <Button asChild variant={ready ? "default" : "outline"} className="h-11">
        <Link to={`/meetings/notes/${meeting.id}`} data-testid="meeting-notes-link">
          {ready ? "Notes Ready" : "Open Notes"}
        </Link>
      </Button>
      <Button type="button" variant="ghost" className="h-11" onClick={done}>
        Close
      </Button>
    </div>
  );
}
